import { Alert, Button, DatePicker, Input, Modal, Space, Table, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import TabbedPage from 'components/tabbed-page';
import { useCurrentRoles } from 'hooks/use-current-roles';
import NoAccess from 'pages/online-reward/components/no-access';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import PolicyEditor from './components/policy-editor';
import { AgencyFeePolicyData, PolicyVersion } from './types';
import { currentUtcMonth, monthLabel, rewardErrorMessage } from './utils';
import styles from './style.module.scss';

const RatesTab = () => {
  const [active, setActive] = useState<PolicyVersion | null>(null);
  const [draft, setDraft] = useState<AgencyFeePolicyData | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [effective, setEffective] = useState<Dayjs>(dayjs(`${currentUtcMonth()}-01`));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    AxiosUtils.get(API.agencyFeePolicies)
      .then((res: any) => {
        setActive(res?.data?.active ?? null);
        const data = JSON.parse(JSON.stringify(res?.data?.active?.data ?? res?.data?.defaults));
        setDraft({ ...data, creatorRules: data.creatorRules ?? [] });
      })
      .catch((err) => setError(rewardErrorMessage(err)));
    AxiosUtils.get(API.agencyFeeGroups).then((res: any) => setGroups(res?.data ?? [])).catch(() => setGroups([]));
  }, []);
  useEffect(load, [load]);

  const save = () =>
    Modal.confirm({
      title: `Lưu thành bản mới, hiệu lực từ ${effective.format('MM/YYYY')}?`,
      content: 'Mọi tháng từ tháng này trở đi (tới khi có bản mới hơn) sẽ tính theo bản mới. Bản cũ vẫn giữ trong Lịch sử bản.',
      okText: 'Lưu bản mới',
      cancelText: 'Huỷ',
      onOk: async () => {
        setSaving(true);
        try {
          const data = draft ? { ...draft, emailAgency: draft.emailAgency.filter((r) => r.email.trim() || r.agency.trim()) } : draft;
          await AxiosUtils.post(API.agencyFeePolicies, { effectiveMonth: effective.format('YYYY-MM'), data, note });
          notification.success({ message: 'Đã lưu bản chính sách mới' });
          setNote('');
          load();
        } catch (err) {
          notification.error({ message: 'Chưa lưu được', description: rewardErrorMessage(err) });
        } finally {
          setSaving(false);
        }
      },
    });

  if (error) return <Alert type="error" showIcon message={error} />;
  if (!draft) return null;
  return (
    <>
      <Alert
        className={styles.section}
        type="info"
        showIcon
        message={`Đang dùng: ${active?.id ? `bản #${active.id}` : 'bản mặc định 2026 (đúng số sheet CHINH SACH của web cũ)'}, hiệu lực từ ${monthLabel(active?.effectiveMonth)}`}
      />
      <PolicyEditor value={draft} groups={groups} onChange={setDraft} />
      <Space wrap className={styles.section} style={{ marginTop: 16 }}>
        <span>Hiệu lực từ tháng</span>
        <DatePicker picker="month" allowClear={false} format="MM/YYYY" value={effective} onChange={(d) => d && setEffective(d)} />
        <Input placeholder="Ghi chú cho bản này" value={note} onChange={(e) => setNote(e.target.value)} style={{ width: 280 }} />
        <Button type="primary" loading={saving} onClick={save}>Lưu thành bản mới</Button>
      </Space>
    </>
  );
};

const HistoryTab = () => {
  const [versions, setVersions] = useState<PolicyVersion[]>([]);
  useEffect(() => {
    AxiosUtils.get(API.agencyFeePolicies).then((res: any) => setVersions(res?.data?.versions ?? [])).catch(() => setVersions([]));
  }, []);
  return (
    <Table
      rowKey="id"
      size="small"
      dataSource={versions}
      locale={{ emptyText: 'Chưa có bản nào — đang dùng bản mặc định 2026.' }}
      columns={[
        { title: 'Bản', dataIndex: 'id', render: (v) => `#${v}` },
        { title: 'Hiệu lực từ', dataIndex: 'effectiveMonth', render: monthLabel },
        { title: 'Ghi chú', dataIndex: 'note' },
        { title: 'Người tạo', dataIndex: 'createdByName' },
        { title: 'Lúc tạo', dataIndex: 'createdAt', render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—') },
        {
          title: '', key: 'view',
          render: (_, v: PolicyVersion) => (
            <Button size="small" onClick={() => Modal.info({ width: 720, title: `Bản #${v.id}`, content: <pre style={{ maxHeight: 480, overflow: 'auto' }}>{JSON.stringify(v.data, null, 2)}</pre> })}>
              Xem
            </Button>
          ),
        },
      ]}
    />
  );
};

/** Phí Agency → Chính sách (chỉ Admin). */
const AgencyFeePolicyPage = () => {
  const { loaded, isAdmin } = useCurrentRoles();
  if (loaded && !isAdmin) return <NoAccess adminOnly />;
  return (
    <TabbedPage
      tabs={[
        { key: 'muc-phi', label: 'Mức phí', render: () => <RatesTab /> },
        { key: 'lich-su', label: 'Lịch sử bản', render: () => <HistoryTab /> },
      ]}
    />
  );
};

export default AgencyFeePolicyPage;
